import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { transportAPI } from '../../services/api';
import AppNavbar from '../../components/AppNavbar';
import SiteFooter from '../../components/SiteFooter';
import MobileBottomNav from '../../components/MobileBottomNav';

const VEHICLE_LABELS = {
  rickshaw: 'ركشة', saloon: 'صالون', fourwheel: 'فوريل', box: 'بوكس', bus: 'حافلة',
  pickup: 'بيكب', light_truck: 'دفار', truck_5ton: 'شاحنة 5 طن', truck_10ton: 'شاحنة 10 طن',
  heavy_truck: 'شاحنة ثقيلة', trailer: 'تريلا', container_carrier: 'ناقلة اليات',
  motorcycle: 'دراجة نارية', taxi: 'تاكسي', van: 'فان',
};

// Deliberately no default sort by price — design doc §11: showing every offer
// side by side with equal weight is the point, not nudging toward "cheapest".
const QuoteCard = ({ quote, onAccept, accepting }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-bold text-gray-800">{quote.provider?.name}</span>
      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{VEHICLE_LABELS[quote.vehicleCategory] || quote.vehicleCategory}</span>
    </div>
    {quote.provider?.ratingCount > 0 && (
      <p className="text-xs text-amber-600 font-semibold mb-1">★ {quote.provider.ratingAvg} ({quote.provider.ratingCount} تقييم)</p>
    )}
    <p className="text-2xl font-black text-blue-700 mb-2">{quote.price} <span className="text-sm font-bold text-gray-400">ج.س</span></p>
    <div className="text-xs text-gray-500 space-y-1 mb-3">
      {quote.availableAt && <p>🕐 التوفر: {new Date(quote.availableAt).toLocaleString('ar-SA')}</p>}
      {quote.estimatedDelivery && <p>🚚 التسليم المتوقع: {quote.estimatedDelivery}</p>}
      {quote.loadingIncluded && <p className="text-emerald-700 font-semibold">✓ التحميل مشمول</p>}
      {quote.notes && <p className="italic">{quote.notes}</p>}
    </div>
    <button onClick={() => onAccept(quote)} disabled={accepting}
      className="w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white text-sm font-bold py-2.5 rounded-xl transition">
      قبول هذا العرض
    </button>
  </div>
);

const TransportRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  // silent=true for background polls — only the very first load shows the
  // full-page spinner, so waiting for new quotes doesn't flash/reset the
  // page every cycle (same fix as RideRequestDetailPage).
  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    transportAPI.getRequestById(id)
      .then((res) => setRequest(res.data.data))
      .catch(() => {})
      .finally(() => { if (!silent) setLoading(false); });
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let interval = setInterval(() => load(true), 15000); // cargo quotes — less time-sensitive than rides
    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        load(true);
        interval = setInterval(() => load(true), 15000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [load]);

  const handleAccept = async (quote) => {
    if (!window.confirm(`تأكيد قبول عرض ${quote.provider?.name} بمبلغ ${quote.price} ج.س؟`)) return;
    setAccepting(true);
    try {
      const res = await transportAPI.acceptQuote(id, quote.id);
      navigate(`/transport/orders/${res.data.data.id}`, { replace: true });
    } catch (err) {
      alert(err.response?.data?.message || 'تعذّر قبول العرض');
    } finally { setAccepting(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm('هل تريد إلغاء طلب النقل هذا؟')) return;
    try {
      await transportAPI.cancelRequest(id);
      navigate('/transport');
    } catch (err) {
      alert(err.response?.data?.message || 'تعذّر إلغاء الطلب');
    }
  };

  if (loading) return <div dir="rtl" className="min-h-screen bg-[#F5F9FF]"><AppNavbar /><p className="text-center py-16 text-gray-400">جاري التحميل...</p></div>;
  if (!request) return <div dir="rtl" className="min-h-screen bg-[#F5F9FF]"><AppNavbar /><p className="text-center py-16 text-gray-400">الطلب غير موجود</p></div>;

  const pendingQuotes = (request.quotes || []).filter((q) => q.status === 'pending');

  return (
    <div dir="rtl" className="min-h-screen bg-[#F5F9FF] pb-16">
      <AppNavbar />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-xs text-gray-400">{request.requestNumber}</span>
            {request.status === 'open_for_quotes' && (
              <button onClick={handleCancel} className="text-xs font-bold text-red-600 hover:underline">إلغاء الطلب</button>
            )}
          </div>
          <p className="font-bold text-gray-800">{request.fromLabel} ← {request.toLabel}</p>
          {request.cargoDescription && <p className="text-xs text-gray-500 mt-1">{request.cargoDescription}</p>}
        </div>

        <h2 className="font-bold text-gray-800 mb-3">
          {pendingQuotes.length > 0 ? `📩 العروض المقدَّمة (${pendingQuotes.length})` : 'بانتظار عروض المزودين'}
        </h2>

        {pendingQuotes.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center text-gray-500">
            لم يصل أي عرض بعد — سيتم إشعارك فور وصول أول عرض
          </div>
        ) : (
          <div className="space-y-4">
            {pendingQuotes.map((q) => <QuoteCard key={q.id} quote={q} onAccept={handleAccept} accepting={accepting} />)}
          </div>
        )}
      </div>
      <div className="hidden md:block"><SiteFooter navigate={navigate} /></div>
      <MobileBottomNav />
    </div>
  );
};

export default TransportRequestDetailPage;
